import React, { FC, useCallback, useRef, useState } from 'react';
import { Button, Paper, Popover, SxProps, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { mergeSx } from '@/utils/mergeSx';
import { Api, BulkEditBody } from '@/services/Api';
import { Page } from '@/declarations/models/Page';
import { useStore } from '@/components/store/Store';
import { Status } from '@/declarations/models/Status';

import ImportedDeleteProgress from './ImportedDeleteProgress';
import { ContentViewContentContext, useContentView } from '../ContentViewContext';
import Styles from '../../../assets/js/Styles';
import Container from '../../../components/Container';

interface PageTreeBulkOperationsProps {
  reloadPages: () => void;
  setBulkLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  bulkOperationInProgress: boolean;
  setBulkOperationInProgress: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ConfirmedBulkButtonProps {
  label: string;
  question: string;
  confirmLabel: string;
  confirmColor: 'primary' | 'error';
  disabled: boolean;
  sx: SxProps<Theme>;
  onConfirm: () => void;
}

/** A bulk button that asks `question` in a popover before running. */
const ConfirmedBulkButton: FC<ConfirmedBulkButtonProps> = ({
  label,
  question,
  confirmLabel,
  confirmColor,
  disabled,
  sx,
  onConfirm,
}) => {
  const { t: tComponents } = useTranslation('components');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button ref={buttonRef} disabled={disabled} sx={sx} variant='contained' onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Popover
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Paper>
          <Container column p={1} sx={{ maxWidth: 420 }}>
            <Typography>{question}</Typography>
            <Container right>
              <Button variant='outlined' color='secondary' onClick={() => setOpen(false)}>
                {tComponents('DeleteButton.AbortOption')}
              </Button>
              <Button
                variant='contained'
                color={confirmColor}
                onClick={() => {
                  setOpen(false);
                  onConfirm();
                }}>
                {confirmLabel}
              </Button>
            </Container>
          </Container>
        </Paper>
      </Popover>
    </>
  );
};

export const PageTreeBulkOperations: FC<PageTreeBulkOperationsProps> = ({
  reloadPages,
  setBulkLoading,
  bulkOperationInProgress,
  setBulkOperationInProgress,
}) => {
  const deleteBtnRef = useRef<HTMLButtonElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const { t: tComponents } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const {
    setSelectionMode,
    selectionState,
    setPages,
    setMovePagesMode,
    setMovePagesInFolderMode,
    movePagesMode,
    contentContext,
    showImported,
    publishImportedPages,
    deleteImportedPages,
  } = useContentView();
  const selectedPageIDs = selectionState.selectedItems?.map((p) => p.id || 0);

  const { state } = useStore();
  const selectedSiteId = state.selectedSite?.id || 0;

  const buttonSx = {
    whiteSpace: 'nowrap',
    minWidth: 'fit-content',
  };

  const buttonBackgroundSx = {
    backgroundColor: Styles.Colors.LIGHTEST_GREY,
    '&.Mui-disabled': { backgroundColor: Styles.Colors.LIGHTEST_GREY },
  };

  const hasPublishedSelected = selectionState.selectedItems.some((page: Page) => page.status === Status.PUBLISHED);

  const handleSaveBulk = useCallback(
    async (body: BulkEditBody): Promise<Array<Page> | void> => {
      if (setBulkLoading) setBulkLoading(true);
      setBulkOperationInProgress(true);
      try {
        const result = await Api.bulkEditPages(selectedSiteId, body).fetchDirect(null);
        if (!result) {
          enqueueSnackbar(tCommon('saveFailed'), { variant: 'error' });
        } else {
          enqueueSnackbar(tCommon('saved'), { variant: 'success' });
          return result.changed;
        }
      } finally {
        if (setBulkLoading) setBulkLoading(false);
        setBulkOperationInProgress(false);
      }
    },
    [setBulkLoading, setBulkOperationInProgress, selectedSiteId, enqueueSnackbar, tCommon],
  );

  const updatePageTree = (savedPages: Array<Page>) => {
    setPages((prev: Array<Page>) => {
      const filteredPages = prev.filter((prevPage) => !savedPages.some((savedPage) => savedPage.id === prevPage.id));
      return [...filteredPages, ...savedPages];
    });
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedPageIDs?.length) {
      handleSaveBulk({ item_ids: selectedPageIDs, item_type: 'page', status: Status.DELETED }).then((savedPages) => {
        if (savedPages) {
          selectionState.deselectAll();
          setSelectionMode(false);
          updatePageTree(savedPages);
          reloadPages();
        }
      });
    }
  };

  const handleCancelDelete = () => setShowDeleteConfirm(false);

  const deleteOperation = (
    <>
      <Button
        ref={deleteBtnRef}
        disabled={bulkOperationInProgress || selectionState.noneSelected || movePagesMode || hasPublishedSelected}
        sx={mergeSx(buttonSx, buttonBackgroundSx)}
        variant='contained'
        onClick={() => setShowDeleteConfirm(true)}>
        {tComponents('PageTreeBulkOperations.delete')}
      </Button>
      <Popover
        id='confirm-delete-dialog'
        anchorEl={deleteBtnRef.current}
        open={showDeleteConfirm}
        onClose={handleCancelDelete}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Paper>
          <Container column p={1}>
            <Typography>{tComponents('DeleteButton.ConfirmDeleteQuestion')}</Typography>
            <Container right>
              <Button variant='outlined' color='secondary' onClick={handleCancelDelete}>
                {tComponents('DeleteButton.AbortOption')}
              </Button>
              <Button variant='contained' color='error' onClick={handleConfirmDelete}>
                {tComponents('DeleteButton.ConfirmOption')}
              </Button>
            </Container>
          </Container>
        </Paper>
      </Popover>
    </>
  );

  // In the imported view both operations take every page under the selected ones along, so each is confirmed with a
  // question that says so when it applies.
  if (showImported) {
    const selectedHaveChildren = selectionState.selectedItems.some((page) => selectionState.hasChildren(page.id || 0));
    const exitSelection = () => {
      selectionState.deselectAll();
      setSelectionMode(false);
    };
    const question = (key: string) =>
      [
        tComponents(`PageTreeBulkOperations.${key}Question`),
        selectedHaveChildren ? tComponents(`PageTreeBulkOperations.${key}ChildrenNote`) : '',
      ]
        .filter(Boolean)
        .join(' ');
    const deleteSelected = () =>
      deleteImportedPages(selectionState.selectedItems, (done, total) => setProgress({ done, total }))
        .then(exitSelection)
        .finally(() => setProgress(null));
    return (
      <Container column>
        <Container>
          <ConfirmedBulkButton
            label={tComponents('PageTreeBulkOperations.publish')}
            question={question('publishImported')}
            confirmLabel={tComponents('PageTreeBulkOperations.publish')}
            confirmColor='primary'
            disabled={bulkOperationInProgress || selectionState.noneSelected}
            sx={mergeSx(buttonSx, buttonBackgroundSx)}
            onConfirm={() => publishImportedPages(selectionState.selectedItems).then(exitSelection)}
          />
          <ConfirmedBulkButton
            label={tComponents('PageTreeBulkOperations.delete')}
            question={question('deleteImported')}
            confirmLabel={tComponents('DeleteButton.ConfirmOption')}
            confirmColor='error'
            disabled={bulkOperationInProgress || selectionState.noneSelected}
            sx={mergeSx(buttonSx, buttonBackgroundSx)}
            onConfirm={deleteSelected}
          />
        </Container>
        {progress && <ImportedDeleteProgress done={progress.done} total={progress.total} />}
      </Container>
    );
  }

  return (
    <Container>
      <Button
        disabled={bulkOperationInProgress || selectionState.noneSelected || movePagesMode}
        sx={mergeSx(buttonSx, buttonBackgroundSx)}
        variant='contained'
        onClick={() => {
          if (selectedPageIDs?.length) {
            handleSaveBulk({ item_ids: selectedPageIDs, item_type: 'page', status: Status.DRAFT }).then(
              (savedPages) => {
                if (savedPages) {
                  selectionState.deselectAll();
                  setSelectionMode(false);
                  updatePageTree(savedPages);
                }
              },
            );
          }
        }}>
        {tComponents('PageTreeBulkOperations.unpublish')}
      </Button>
      <Button
        disabled={bulkOperationInProgress || selectionState.noneSelected || movePagesMode}
        sx={mergeSx(buttonSx, buttonBackgroundSx)}
        variant='contained'
        onClick={() => {
          if (selectedPageIDs?.length) {
            handleSaveBulk({ item_ids: selectedPageIDs, item_type: 'page', status: Status.ARCHIVED }).then(
              (savedPages) => {
                if (savedPages) {
                  selectionState.deselectAll();
                  setSelectionMode(false);
                  updatePageTree(savedPages);
                }
              },
            );
          }
        }}>
        {tComponents('PageTreeBulkOperations.archive')}
      </Button>
      {contentContext === ContentViewContentContext.EVENT && deleteOperation}
      {contentContext === ContentViewContentContext.PAGE && (
        <Button
          disabled={bulkOperationInProgress || selectionState.noneSelected || movePagesMode}
          sx={mergeSx(buttonSx, buttonBackgroundSx)}
          variant='contained'
          onClick={() => setMovePagesMode((prevState) => !prevState)}>
          {tComponents('PageTreeBulkOperations.movePages')}
        </Button>
      )}
      {contentContext === ContentViewContentContext.EVENT && (
        <Button
          disabled={bulkOperationInProgress || selectionState.noneSelected || movePagesMode}
          sx={mergeSx(buttonSx, buttonBackgroundSx)}
          variant='contained'
          onClick={() => setMovePagesInFolderMode((prevState) => !prevState)}>
          {tComponents('PageTreeBulkOperations.moveEvents')}
        </Button>
      )}
    </Container>
  );
};

export default PageTreeBulkOperations;
